import styled from "styled-components";
import {
  documentCoverageNotes,
  documentsNotInReport,
  sourceCoverage,
} from "../../utils/sourceCoverage";

const Panel = styled.details`
  margin: 16px 0;
  padding: 12px 16px;
  border: 1px solid var(--brand-border, #dce5e4);
  border-radius: 10px;
  background: #f8faf9;
  color: #344b49;
  font-size: 0.88rem;
  line-height: 1.5;
  overflow-wrap: anywhere;
  summary { cursor: pointer; font-weight: 700; }
  h3 { font-size: 0.95rem; margin: 16px 0 6px; }
  p { margin: 8px 0; }
  ul { margin: 8px 0; padding-left: 20px; }
  li { margin: 8px 0; }
`;

/** This private input record never belongs on a seller's token-shared page.
 * @param {{doc: Record<string, any>}} props
 */
export default function SourcesUsed({ doc }) {
  if (doc.editable !== true) return null;
  const coverage = sourceCoverage(doc.source_coverage);
  const pending = documentsNotInReport(doc.source_coverage, doc.input_documents);
  /** @type {Record<string, string>} */
  const labels = {
    read: "Text included",
    partial: "Partly included",
    unreadable: "Text could not be read",
    omitted: "Not included",
  };
  return (
    <Panel aria-label="Sources used">
      <summary>Sources used</summary>
      {!coverage ? (
        <p>Source coverage was not recorded for this report, or its record is unavailable.</p>
      ) : (
        <>
          <p>
            These are the recorded inputs supplied to the full analysis, not a guarantee that every
            detail was addressed or verified. Later uploads do not change this record.
          </p>
          <h3>Photos</h3>
          {coverage.photos === null ? (
            <p>Photo coverage was not recorded for the full analysis.</p>
          ) : (
            <>
              <p>
                {coverage.photos.supplied_count} of {coverage.photos.inventory_count} saved photos
                were supplied to the full analysis.
              </p>
              {coverage.photos.source_found_count !== null && (
                <p>
                  Zillow returned {coverage.photos.source_found_count} photos
                  {coverage.photos.imported_count !== null
                    ? `; ${coverage.photos.imported_count} were imported`
                    : ""}
                  .
                  {coverage.photos.import_selected_count !== null
                    ? ` ${coverage.photos.import_selected_count} were selected for import.`
                    : ""}
                  {coverage.photos.import_limit_omitted_count > 0
                    ? ` ${coverage.photos.import_limit_omitted_count} were outside the import limit.`
                    : ""}
                  {coverage.photos.import_failed_count > 0
                    ? ` ${coverage.photos.import_failed_count} selected photos could not be imported.`
                    : ""}
                </p>
              )}
              {coverage.photos.supplied.length > 0 && (
                <p>
                  Supplied photos:{" "}
                  {coverage.photos.supplied
                    .map((/** @type {any} */ row) => row.filename)
                    .join(", ")}
                </p>
              )}
            </>
          )}
          {coverage.last_photo_revision !== null && (
            <>
              <p>
                The latest recorded photo-section revision separately received{" "}
                {coverage.last_photo_revision.supplied_count} of{" "}
                {coverage.last_photo_revision.inventory_count} saved photos. This does not mean the
                rest of the report was re-analyzed.
              </p>
              {coverage.last_photo_revision.supplied.length > 0 && (
                <p>
                  Supplied for the latest recorded photo revision:{" "}
                  {coverage.last_photo_revision.supplied
                    .map((/** @type {any} */ row) => row.filename)
                    .join(", ")}
                </p>
              )}
            </>
          )}
          <h3>Documents</h3>
          {coverage.documents === null ? (
            <p>Document coverage was not recorded for the full analysis.</p>
          ) : coverage.documents.length === 0 ? (
            <p>No documents were supplied to the full analysis.</p>
          ) : (
            <ul>
              {coverage.documents.map((/** @type {any} */ row) => (
                <li key={row.id}>
                  <strong>{row.filename}</strong>: {labels[row.status]}.
                  <p>
                    {row.pages_examined}
                    {row.page_count === null
                      ? " pages examined"
                      : ` of ${row.page_count} pages examined`}
                    ; {row.pages_with_text} had extractable text.{" "}
                    {row.chars_included.toLocaleString()} characters were included in the analysis
                    input.
                  </p>
                  {documentCoverageNotes(row).map((/** @type {string} */ note) => (
                    <p key={note}>{note}</p>
                  ))}
                </li>
              ))}
            </ul>
          )}
          {pending.length > 0 && (
            <>
              <h3>Not included in this report</h3>
              <ul>
                {pending.map((row) => (
                  <li key={row.id}>{row.filename}</li>
                ))}
              </ul>
              <p>
                These current uploads are not in the report's recorded input set. Re-prepare from
                the editor to include them in a new full analysis; uploading alone does not do this.
              </p>
            </>
          )}
          <p>
            Document coverage concerns extractable PDF text, not OCR or a review of every diagram.
            Review important details against the originals.
          </p>
        </>
      )}
    </Panel>
  );
}
